/* eslint-disable no-console*/
import type {
  ControlConfirmDataDto,
  DownloadMissionConfirmDataDto,
  MqttControlExportDto,
  MqttDownloadMissionConfirmEventDto,
  DiagnosticResponseExportDto,
  MqttMessageExportDto,
  MqttMissionStatusEventDto,
  DiagnosticNotificationExportDto,
  MqttPhotoesExportDto,
} from 'api/asyncapi'
import EventEmitter from 'utils/EventEmitter'

export enum SOCKET_MESSAGES {
  MQTT_MESSAGES = 'mqtt.message',
  MQTT_CONTROL = 'mqtt.control',
  ROBOT_PHOTOES = 'mqtt.photoes',
  MISSION_STATUS = 'mqtt.mission_status',
  DOWNLOAD_MISSION_CONFIRM = 'mqtt.download_mission_confirm',
  DIAGNOSTIC_NOTIFICATION = 'diagnostic.notification',
  DIAGNOSTIC_RESPONSE = 'diagnostic.response',
}

/**
 * Discriminated union of all server-to-client WebSocket events.
 * The `type` field is used as the discriminator.
 *
 * Для diagnostic.notification подписчик получает полный конверт события
 * (включая `data`), т.ч. идентификатор робота извлекается из уведомления.
 */
export type WebSocketServerEvent =
  | (MqttMessageExportDto & { type: 'mqtt.message' })
  | (MqttControlExportDto & { type: 'mqtt.control' })
  | (MqttPhotoesExportDto & { type: 'mqtt.photoes' })
  | (MqttMissionStatusEventDto & { type: 'mqtt.mission_status' })
  | (MqttDownloadMissionConfirmEventDto & { type: 'mqtt.download_mission_confirm' })
  | (DiagnosticNotificationExportDto & { type: 'diagnostic.notification' })
  | (DiagnosticResponseExportDto & { type: 'diagnostic.response' })

/**
 * Map from SOCKET_MESSAGES enum value to the corresponding data payload type.
 */
export interface WebSocketMessageMap {
  [SOCKET_MESSAGES.MQTT_MESSAGES]: MqttMessageExportDto['data']
  [SOCKET_MESSAGES.MQTT_CONTROL]: ControlConfirmDataDto
  [SOCKET_MESSAGES.ROBOT_PHOTOES]: MqttPhotoesExportDto['data']
  [SOCKET_MESSAGES.MISSION_STATUS]: MqttMissionStatusEventDto['data']
  [SOCKET_MESSAGES.DOWNLOAD_MISSION_CONFIRM]: DownloadMissionConfirmDataDto
  [SOCKET_MESSAGES.DIAGNOSTIC_NOTIFICATION]: DiagnosticNotificationExportDto
  [SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE]: DiagnosticResponseExportDto
}

const TIME_TO_RECONNECT = 2000

/** Максимум повторных попыток отправить сообщение, пока сокет не открылся. */
const MAX_SEND_RETRIES = 30

const WEBSOCKET_PATH = '/api/websocket'

interface WebSocketConnection {
  path: string
  eventEmitter: EventEmitter
  socket: WebSocket
  opened: boolean
}

/**
 * Менеджер нескольких одновременных WebSocket-соединений.
 * Ключ соединения — путь (например, '/notifications/' или '/robot/5').
 * У каждого соединения свой EventEmitter, свой реконнект и свои подписчики.
 */
class WebsocketManager {
  private static connections: Map<string, WebSocketConnection> = new Map()
  private static defaultPath?: string

  static init(path: string): void {
    if (WebsocketManager.connections.has(path)) return
    if (!WebsocketManager.defaultPath) WebsocketManager.defaultPath = path

    const connection: WebSocketConnection = {
      path,
      eventEmitter: new EventEmitter(),
      socket: undefined as unknown as WebSocket,
      opened: false,
    }
    connection.socket = WebsocketManager.createSocket(connection)
    WebsocketManager.connections.set(path, connection)
  }

  private static createSocket(connection: WebSocketConnection): WebSocket {
    const scheme = 'ws' + (window.location.protocol === 'https:' ? 's' : '')
    const websocketUrl = `${scheme}://${window.location.host}${WEBSOCKET_PATH}${connection.path}`

    console.log('Connecting to:', websocketUrl)

    const socket = new WebSocket(websocketUrl)
    socket.onmessage = message => {
      try {
        const messageParsed: WebSocketServerEvent = JSON.parse(message.data)
        if (
          messageParsed.type === SOCKET_MESSAGES.DIAGNOSTIC_NOTIFICATION ||
          messageParsed.type === SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE
        ) {
          // Для diagnostic-событий отдаём весь конверт, чтобы был доступен идентификатор робота из данных.
          connection.eventEmitter.emit(messageParsed.type, messageParsed)
        } else {
          connection.eventEmitter.emit(messageParsed.type, messageParsed.data)
        }
      } catch (error) {
        console.error(error)
      }
    }
    socket.onopen = () => {
      console.log('Socket connected:', websocketUrl)
      connection.opened = true
    }
    socket.onclose = () => {
      connection.opened = false
      if (!WebsocketManager.connections.has(connection.path)) return
      setTimeout(() => {
        if (!WebsocketManager.connections.has(connection.path)) return
        connection.socket = WebsocketManager.createSocket(connection)
      }, TIME_TO_RECONNECT)
    }
    return socket
  }

  private static getConnection(path?: string): WebSocketConnection {
    const key = path || WebsocketManager.defaultPath
    const connection = key ? WebsocketManager.connections.get(key) : undefined
    if (!connection) {
      throw new Error(`WebsocketManager: no connection for path '${key ?? 'default'}'`)
    }
    return connection
  }

  static addEventListener<T extends SOCKET_MESSAGES>(
    message: T,
    listener: (data: WebSocketMessageMap[T]) => void,
    path?: string
  ): void {
    WebsocketManager.getConnection(path).eventEmitter.addListener(message, listener)
  }

  static removeEventListener<T extends SOCKET_MESSAGES>(
    message: T,
    listener: (data: WebSocketMessageMap[T]) => void,
    path?: string
  ): void {
    const key = path || WebsocketManager.defaultPath
    const connection = key ? WebsocketManager.connections.get(key) : undefined
    connection?.eventEmitter.removeListener(message, listener)
  }

  static sendMessage(data: string, path?: string, attempt = 0): void {
    const connection = WebsocketManager.getConnection(path)
    if (connection.opened) {
      connection.socket.send(data)
    } else if (attempt < MAX_SEND_RETRIES) {
      setTimeout(() => WebsocketManager.sendMessage(data, path, attempt + 1), 1000)
    }
  }

  static disconnect(path?: string): void {
    if (path) {
      const connection = WebsocketManager.connections.get(path)
      if (connection && connection.socket.readyState !== WebSocket.CLOSED) {
        connection.socket.close()
      }
      WebsocketManager.connections.delete(path)
      if (WebsocketManager.defaultPath === path) {
        WebsocketManager.defaultPath = WebsocketManager.connections.keys().next().value
      }
      return
    }

    Array.from(WebsocketManager.connections.values()).forEach(connection => {
      if (connection.socket.readyState !== WebSocket.CLOSED) {
        connection.socket.close()
      }
    })
    WebsocketManager.connections.clear()
    WebsocketManager.defaultPath = undefined
  }
}

export default WebsocketManager
